// Stripe-style insight widgets — big numbers, sparkline backdrops, soft depth.
import { useMemo } from "react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Plus } from "lucide-react";

const fmtMoney = (n, sym = "$") =>
  `${sym}${(Math.abs(n) >= 1e6
    ? (n / 1e6).toFixed(1) + "M"
    : Math.abs(n) >= 1e3
    ? (n / 1e3).toFixed(1) + "k"
    : Math.round(n).toLocaleString())}`;

const fmtPct = (n) => `${n.toFixed(1)}%`;

// ═════════════════════════════════════════════════════════════════════
// KPICard — Stripe-inspired
// ═════════════════════════════════════════════════════════════════════
export function KPICard({ label, value, prior, deltaPct, sparkline, format = "money", currency = "$", accent = "#635BFF", sub, testid }) {
  const displayValue =
    format === "money" ? fmtMoney(value, currency)
    : format === "pct" ? fmtPct(value)
    : format === "days" ? `${value}d`
    : Number(value).toLocaleString();

  const trendPositive = deltaPct > 0.05;
  const trendNegative = deltaPct < -0.05;
  const TrendIcon = trendPositive ? TrendingUp : trendNegative ? TrendingDown : Minus;
  const trendColor = trendPositive ? "#00A66E" : trendNegative ? "#E5484D" : "var(--pg-text-mute)";

  const gradId = useMemo(() => `spark-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden"
      style={{
        background: "var(--pg-surface)",
        border: "1px solid var(--pg-border)",
        borderRadius: 14,
        padding: "20px 22px",
        boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 4px 12px rgba(15,15,15,0.03)",
        minHeight: 132,
      }}
    >
      {sparkline && sparkline.length > 0 && (
        <div style={{
          position: "absolute", inset: 0, opacity: 0.35,
          pointerEvents: "none", padding: "60px 0 0 0",
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.5}
                    fill={`url(#${gradId})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="relative">
        <div style={{
          fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
          color: "var(--pg-text-mute)", fontWeight: 600, marginBottom: 8,
        }}>{label}</div>
        <div style={{
          fontSize: 28, fontWeight: 700, color: "var(--pg-text)",
          fontFeatureSettings: '"tnum" 1', letterSpacing: "-0.02em", lineHeight: 1.1,
        }}>{displayValue}</div>
        {sub && (
          <div style={{ fontSize: 11, color: "var(--pg-text-mute)", marginTop: 4 }}>{sub}</div>
        )}
        {typeof deltaPct === "number" && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 12, fontWeight: 600, marginTop: 10,
            color: trendColor, fontFeatureSettings: '"tnum" 1',
          }}>
            <TrendIcon size={13} strokeWidth={2.5} />
            {Math.abs(deltaPct).toFixed(1)}%
            <span style={{ color: "var(--pg-text-mute)", fontWeight: 500, marginLeft: 4 }}>
              vs prior
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// ForecastChart — stacked area + target line
// ═════════════════════════════════════════════════════════════════════
export function ForecastChart({ data, targetLine, kind = "total", currency = "$", onAddTarget }) {
  const dataKeys = {
    total: { won: "total_won", forecast: "total_forecast", open: "total_open", target: "target_total" },
    acquisition: { won: "acquisition_won", forecast: "acquisition_forecast", open: "acquisition_open", target: "target_acquisition" },
    growth: { won: "growth_won", forecast: "growth_forecast", open: "growth_open", target: "target_growth" },
  }[kind];

  const wonColor = "#00A66E";
  const forecastColor = "#635BFF";
  const openColor = "#B8B0FF";

  return (
    <div
      style={{
        background: "var(--pg-surface)",
        border: "1px solid var(--pg-border)",
        borderRadius: 14, padding: "22px 22px 12px",
        boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 4px 12px rgba(15,15,15,0.03)",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em",
                        color: "var(--pg-text-mute)", fontWeight: 600 }}>
            Rolling Forecast · 12 Months
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--pg-text)", marginTop: 2 }}>
            {kind === "total" ? "Total (Acquisition + Growth)"
             : kind === "acquisition" ? "Acquisition only"
             : "Growth only"}
          </div>
        </div>
        {onAddTarget && (
          <button
            onClick={() => onAddTarget(kind)}
            data-testid={`btn-add-target-${kind}`}
            className="pg-btn"
            style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}
          >
            <Plus size={13} /> Set target
          </button>
        )}
      </div>

      <div className="flex gap-4 mb-2" style={{ fontSize: 11, color: "var(--pg-text-mute)" }}>
        <LegendSwatch color={wonColor} label="Won" />
        <LegendSwatch color={forecastColor} label="Weighted Forecast" />
        <LegendSwatch color={openColor} label="Open Pipeline (unweighted)" />
        {targetLine > 0 && <LegendSwatch color="#E5484D" label="Monthly Target" dashed />}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--pg-border)" vertical={false} strokeDasharray="2 4" />
          <XAxis dataKey="month" stroke="var(--pg-text-mute)"
                 tick={{ fontSize: 11, fill: "var(--pg-text-mute)" }}
                 tickFormatter={(m) => m.slice(-2) + "/" + m.slice(2, 4)}
                 tickLine={false} axisLine={false} />
          <YAxis stroke="var(--pg-text-mute)"
                 tick={{ fontSize: 11, fill: "var(--pg-text-mute)" }}
                 tickFormatter={(v) => fmtMoney(v, currency)}
                 tickLine={false} axisLine={false} width={54} />
          <Tooltip
            contentStyle={{
              background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
              borderRadius: 10, fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
            formatter={(v, name) => [fmtMoney(v, currency), name]}
          />
          <Bar dataKey={dataKeys.won} name="Won" stackId="a" fill={wonColor} radius={[0, 0, 0, 0]} />
          <Bar dataKey={dataKeys.forecast} name="Forecast" stackId="a" fill={forecastColor} radius={[6, 6, 0, 0]} />
          {targetLine > 0 && (
            <ReferenceLine y={targetLine} stroke="#E5484D" strokeDasharray="4 3"
                           strokeWidth={1.5} label={{ value: "Target", position: "right", fill: "#E5484D", fontSize: 11 }} />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LegendSwatch({ color, label, dashed }) {
  return (
    <div className="flex items-center gap-1.5">
      <span style={{ width: 10, height: 10, background: dashed ? "transparent" : color,
                     border: dashed ? `2px dashed ${color}` : "none",
                     borderRadius: 2, display: "inline-block" }} />
      <span>{label}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// ActivityBoard — per-rep grouped bars
// ═════════════════════════════════════════════════════════════════════
export function ActivityBoard({ rows }) {
  if (!rows || rows.length === 0) {
    return <EmptyState label="No activity yet in this period." />;
  }
  return (
    <div style={{
      background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
      borderRadius: 14, padding: 22,
      boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 4px 12px rgba(15,15,15,0.03)",
    }}>
      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em",
                    color: "var(--pg-text-mute)", fontWeight: 600, marginBottom: 4 }}>
        Activity by Rep
      </div>
      <div style={{ fontSize: 11, color: "var(--pg-text-mute)", marginBottom: 16 }}>
        Emails sent · Calls made · Moves completed · Response rates
      </div>
      <table className="pg-table" data-testid="activity-table">
        <thead>
          <tr>
            <th>Rep</th>
            <th className="text-right">Emails</th>
            <th className="text-right">Open %</th>
            <th className="text-right">Click %</th>
            <th className="text-right">Calls</th>
            <th className="text-right">Moves ✓</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.user_id}>
              <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{r.user_name}</td>
              <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1' }}>{r.emails_sent}</td>
              <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1', color: r.open_rate >= 40 ? "#00A66E" : "var(--pg-text)" }}>{r.open_rate}%</td>
              <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1', color: r.click_rate >= 8 ? "#00A66E" : "var(--pg-text)" }}>{r.click_rate}%</td>
              <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1' }}>{r.calls_made}</td>
              <td className="text-right" style={{ fontFeatureSettings: '"tnum" 1' }}>{r.moves_completed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FunnelWidget — horizontal bar funnel with drop-off %s
// ═════════════════════════════════════════════════════════════════════
export function FunnelWidget({ pipelineName, stages, currency = "$" }) {
  if (!stages || stages.length === 0) {
    return <EmptyState label="No pipeline data yet." />;
  }
  const max = Math.max(...stages.map(s => s.count), 1);
  return (
    <div style={{
      background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
      borderRadius: 14, padding: 22,
      boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 4px 12px rgba(15,15,15,0.03)",
    }} data-testid="funnel-widget">
      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em",
                    color: "var(--pg-text-mute)", fontWeight: 600 }}>
        Stage Conversion · {pipelineName}
      </div>
      <div style={{ fontSize: 11, color: "var(--pg-text-mute)", marginBottom: 18, marginTop: 2 }}>
        Where deals move — and where they stall.
      </div>
      <div className="space-y-3">
        {stages.map((s, i) => {
          const pct = (s.count / max) * 100;
          return (
            <div key={s.stage_id}>
              <div className="flex justify-between items-baseline mb-1">
                <span style={{ fontWeight: 600, color: "var(--pg-text)", fontSize: 13 }}>
                  {s.stage_name}
                  <span style={{ color: "var(--pg-text-mute)", fontWeight: 500, marginLeft: 6, fontSize: 11 }}>
                    ({s.probability}%)
                  </span>
                </span>
                <span style={{ fontFeatureSettings: '"tnum" 1', color: "var(--pg-text-mute)", fontSize: 12 }}>
                  <b style={{ color: "var(--pg-text)" }}>{s.count}</b> · {fmtMoney(s.value, currency)}
                  {i > 0 && (
                    <span style={{
                      marginLeft: 8, padding: "2px 6px", borderRadius: 4,
                      background: s.conversion_from_prev >= 50 ? "rgba(0,166,110,0.1)" : "rgba(229,72,77,0.1)",
                      color: s.conversion_from_prev >= 50 ? "#00A66E" : "#E5484D",
                      fontSize: 10, fontWeight: 700,
                    }}>
                      {s.conversion_from_prev}%
                    </span>
                  )}
                </span>
              </div>
              <div style={{ height: 8, background: "var(--pg-bg)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.max(pct, 2)}%`, height: "100%",
                  background: `linear-gradient(90deg, #635BFF 0%, #B8B0FF 100%)`,
                  borderRadius: 4,
                  transition: "width 400ms cubic-bezier(0.4,0,0.2,1)",
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// AttainmentBar — used inline in the KPI row
// ═════════════════════════════════════════════════════════════════════
export function AttainmentCard({ attainment, currency = "$" }) {
  const pct = attainment.value || 0;
  const color = pct >= 100 ? "#00A66E" : pct >= 70 ? "#635BFF" : pct >= 40 ? "#F5A524" : "#E5484D";

  return (
    <div data-testid="attainment-card"
         style={{
           background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
           borderRadius: 14, padding: "20px 22px",
           boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 4px 12px rgba(15,15,15,0.03)",
           minHeight: 132, position: "relative", overflow: "hidden",
         }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                    color: "var(--pg-text-mute)", fontWeight: 600, marginBottom: 8 }}>
        Target Attainment
      </div>
      {attainment.target > 0 ? (
        <>
          <div style={{ fontSize: 28, fontWeight: 700, color, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            {pct.toFixed(1)}%
          </div>
          <div style={{ fontSize: 11, color: "var(--pg-text-mute)", marginTop: 4 }}>
            {fmtMoney(attainment.actual, currency)} of {fmtMoney(attainment.target, currency)}
          </div>
          <div style={{ height: 6, background: "var(--pg-bg)", borderRadius: 3, overflow: "hidden", marginTop: 12 }}>
            <div style={{
              width: `${Math.min(pct, 100)}%`, height: "100%", background: color,
              transition: "width 400ms cubic-bezier(0.4,0,0.2,1)",
            }} />
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, color: "var(--pg-text-mute)", marginTop: 6, fontWeight: 500 }}>
            No target set
          </div>
          <div style={{ fontSize: 11, color: "var(--pg-text-mute)", marginTop: 4 }}>
            Set a monthly target from the forecast widget below to track attainment.
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// EmptyState
// ═════════════════════════════════════════════════════════════════════
function EmptyState({ label }) {
  return (
    <div style={{
      background: "var(--pg-surface)", border: "1px dashed var(--pg-border)",
      borderRadius: 14, padding: "40px 22px", textAlign: "center",
      color: "var(--pg-text-mute)", fontSize: 13,
    }}>
      {label}
    </div>
  );
}
